import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/** Which menu the person uses. 'classic' = the side drawer opened with the round/hamburger button (as before). 'dock' = bottom dock on phones, slim rail on tablets and desktops. */
export type MenuStyle = 'classic' | 'dock';
/** Artwork of the icons on the new surfaces (dock, rail, More sheet). 'classic' keeps the PrimeIcons font. */
export type IconSet = 'classic' | 'lucide' | 'phosphor';
/** Phone only: 'shown' dock + header; 'dock-hidden' only a thin restore strip; 'focus' header hidden too (the strip stays). */
export type PhoneBars = 'shown' | 'dock-hidden' | 'focus';
/** Tablet / desktop rail: narrow (opens over the page on hover), pinned open beside the page, or hidden except a small tab. */
export type RailMode = 'collapsed' | 'pinned' | 'hidden';

const KEY_STYLE = 'llMenuStyle';
const KEY_ICONS = 'llIconSet';
const KEY_BARS = 'llPhoneBars';
const KEY_RAIL = 'llRailMode';
const KEY_HDR = 'llHeaderCollapsed';
const PHONE_MAX_WIDTH = 767;

function readChoice<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return (allowed as readonly string[]).includes(v ?? '') ? (v as T) : fallback;
  } catch {
    return fallback; // storage blocked: the default applies for this visit
  }
}

function writeChoice(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage blocked or full: the choice applies until the page is reloaded */
  }
}

/** Pages with their own bottom bar (the Stock Unit tally: status bar, keypad, read-aloud player) never get the dock. The Stock Units LIST does. */
export function urlAllowsDock(url: string | null | undefined): boolean {
  const path = (url ?? '').split('?')[0].split('#')[0];
  if (path.startsWith('/new-tallysheet') && path !== '/new-tallysheet/transport-unit/view') return false;
  return true;
}

/**
 * The one place that knows how the menu is shown (2026-10-07). Everything is remembered per browser; nothing is sent to the server.
 * Classic is the default, so a person who never touches the switch sees no change at all.
 */
@Injectable({ providedIn: 'root' })
export class MenuStyleService {
  private readonly router = inject(Router);

  // The dock / side rail is the menu for everyone from 2026-10-07; a person who chose the classic menu keeps it (the choice is stored on this device).
  readonly style = signal<MenuStyle>(readChoice<MenuStyle>(KEY_STYLE, ['classic', 'dock'], 'dock'));
  // One icon set for everyone (2026-10-07): Lucide. The choice is set here in code; the old per-person switch and any stored value are no longer used.
  readonly iconSet = signal<IconSet>('lucide');
  readonly bars = signal<PhoneBars>(readChoice<PhoneBars>(KEY_BARS, ['shown', 'dock-hidden', 'focus'], 'shown'));
  readonly railMode = signal<RailMode>(readChoice<RailMode>(KEY_RAIL, ['collapsed', 'pinned', 'hidden'], 'collapsed'));

  /** Phone: the header collapsed by hand (a thin strip at the very top brings it back). Independent of the menu style. */
  readonly headerCollapsed = signal<boolean>(readChoice<'0' | '1'>(KEY_HDR, ['0', '1'], '0') === '1');

  private readonly width = signal(typeof window !== 'undefined' ? window.innerWidth : 1024);
  private readonly url = signal(this.router.url);

  readonly isPhone = computed(() => this.width() <= PHONE_MAX_WIDTH);
  readonly dockOn = computed(() => this.style() === 'dock');
  /** Bottom dock layout (phone width). */
  readonly dockPhone = computed(() => this.dockOn() && this.isPhone());
  /** Side rail layout (tablet and wider). */
  readonly railDesktop = computed(() => this.dockOn() && !this.isPhone());
  readonly routeAllowsDock = computed(() => urlAllowsDock(this.url()));
  readonly dockShown = computed(() => this.dockPhone() && this.routeAllowsDock() && this.bars() === 'shown');
  /** Thin restore strip at the very bottom while the dock is hidden by hand. */
  readonly stripShown = computed(() => this.dockPhone() && this.routeAllowsDock() && this.bars() !== 'shown');
  /** Header hidden too (Focus mode). Only ever where the strip can bring it back. */
  readonly focusActive = computed(() => this.dockPhone() && this.routeAllowsDock() && this.bars() === 'focus');
  /** The header is not drawn: collapsed by hand, or Focus mode. Phone only; a thin strip at the top brings it back. */
  readonly headerHidden = computed(() => this.isPhone() && (this.headerCollapsed() || this.focusActive()));
  /** The header's menu button: gone while the dock or the rail already is the menu; back wherever the dock is not shown. */
  readonly headerMenuButton = computed(() => !(this.dockShown() || this.railDesktop()));

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.url.set(e.urlAfterRedirects || e.url));
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        const w = window.innerWidth;
        if (w !== this.width()) this.width.set(w);
      });
    }
    // Page-level hooks for the CSS: space the dock takes, so pages and floating buttons can stay clear of it (see styles.css).
    effect(() => {
      const root = document.documentElement.classList;
      root.toggle('ll-dock', this.dockShown());
      root.toggle('ll-dock-strip', this.stripShown());
      root.toggle('ll-focus', this.focusActive());
      root.toggle('ll-hdr-hidden', this.headerHidden());
      root.toggle('ll-rail', this.railDesktop());
    });
  }

  setStyle(value: MenuStyle): void {
    this.style.set(value);
    writeChoice(KEY_STYLE, value);
  }

  toggleStyle(): void {
    this.setStyle(this.style() === 'dock' ? 'classic' : 'dock');
  }

  setIconSet(value: IconSet): void {
    this.iconSet.set(value);
    writeChoice(KEY_ICONS, value);
  }

  setBars(value: PhoneBars): void {
    this.bars.set(value);
    writeChoice(KEY_BARS, value);
  }

  setHeaderCollapsed(value: boolean): void {
    this.headerCollapsed.set(value);
    writeChoice(KEY_HDR, value ? '1' : '0');
  }

  /** Top strip tapped: the header comes back, and so does everything Focus mode had hidden. */
  restoreHeader(): void {
    this.setHeaderCollapsed(false);
    if (this.bars() === 'focus') this.setBars('shown');
  }

  /** Bottom strip tapped: the dock comes back (and, after Focus mode, the header too). */
  restoreBars(): void {
    if (this.bars() === 'focus') this.setHeaderCollapsed(false);
    this.setBars('shown');
  }

  setRailMode(value: RailMode): void {
    this.railMode.set(value);
    writeChoice(KEY_RAIL, value);
  }
}
